// Utils de formateo
const pad2 = (n: number) => n.toString().padStart(2, '0');

const MONTH_NAMES_ES = [
  'Enero',
  'Febrero',
  'Marzo',
  'Abril',
  'Mayo',
  'Junio',
  'Julio',
  'Agosto',
  'Septiembre',
  'Octubre',
  'Noviembre',
  'Diciembre',
];

const DOW_INPUTS: Record<string, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

const DOW_ES = [
  'Domingo',
  'Lunes',
  'Martes',
  'Miércoles',
  'Jueves',
  'Viernes',
  'Sábado',
];

/**
 * Obtiene componentes locales (en la zona horaria tz) a partir de un Date UTC.
 */
function getLocalParts(utcDate: Date, tz: string) {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    weekday: 'short',
  }).formatToParts(utcDate);

  const map: Record<string, string> = {};
  for (const p of parts) map[p.type] = p.value; // year, month, day, hour, minute, weekday

  const y = parseInt(map.year, 10);
  const m = parseInt(map.month, 10); // 1-12
  const d = parseInt(map.day, 10);
  const hh = parseInt(map.hour, 10);
  const mm = parseInt(map.minute, 10);

  // weekday en-GB: "Mon", "Tue", etc.
  const weekdayShort = map.weekday; // e.g. "Mon"
  const dow =
    DOW_INPUTS[weekdayShort] ?? new Date(Date.UTC(y, m - 1, d)).getUTCDay();

  return { y, m, d, hh, mm, dow };
}

/**
 * Crea un Date en UTC desde componentes (año, mes1-12, día1-31, hh, mm) interpretados como UTC.
 */
function makeUtcDate(y: number, m1: number, d: number, hh: number, mm: number) {
  return new Date(Date.UTC(y, m1 - 1, d, hh, mm, 0, 0));
}

/**
 * Convierte "HH:MM" (UTC) a componentes locales para una fecha de referencia UTC dada.
 */
function convertUtcHHMMToLocalOn(utcRef: Date, hhmm: string, tz: string) {
  const [hStr, mStr] = hhmm.split(':');
  const H = parseInt(hStr, 10) || 0;
  const M = parseInt(mStr, 10) || 0;

  const refY = utcRef.getUTCFullYear();
  const refM = utcRef.getUTCMonth() + 1;
  const refD = utcRef.getUTCDate();

  const atUtc = makeUtcDate(refY, refM, refD, H, M);
  return getLocalParts(atUtc, tz);
}

/**
 * ¿Es el último día de un mes en una zona local?
 */
function isLastDayLocal(
  y: number,
  m1: number,
  d: number,
  tz: string,
  hh: number,
  mm: number
) {
  // Tomamos las 12:00 locales para evitar problemas al borde del día; luego extraemos último día real del mes local.
  // Para hacerlo sin Temporal, calculamos el último día "calendario" local: avanzamos 1 día desde (y,m,d) local y vemos si cambia de mes.
  // Simplificación fiable: compara d con el "last day" del mes local reconstruyendo con medianoche local vía Intl.
  const lastDay = new Date(Date.UTC(y, m1, 0)).getUTCDate(); // último día (en calendario) del mes (en UTC)
  // Esto da el último día "numérico" de ese mes (28-31). Suficiente para decidir si d es último día del mes local,
  // porque el número de días del mes no depende de la zona horaria.
  return d === lastDay;
}

/**
 * Detección robusta de la zona horaria local.
 */
function getUserTimeZone(fallback = 'Europe/Madrid') {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || fallback;
  } catch {
    return fallback;
  }
}

/**
 * Formatea una expresión de TimeSpan en hora LOCAL del usuario (corrigiendo desfases y cruces de día/mes).
 * Acepta opcionalmente forzar una zona horaria (IANA).
 */
export function formatTimeSpanExpression(
  expression: string | null | undefined,
  opts?: { timeZone?: string }
): string {
  if (!expression) return '—';

  const tz = opts?.timeZone ?? getUserTimeZone('Europe/Madrid');

  try {
    // DAILY@HH:MM (hora UTC → mostrar en local)
    if (expression.startsWith('DAILY@')) {
      const timeUtc = expression.slice('DAILY@'.length);
      // Usamos una fecha fija de referencia (p. ej., 2000-01-01) para convertir solo la hora.
      const local = convertUtcHHMMToLocalOn(
        new Date(Date.UTC(2000, 0, 1)),
        timeUtc,
        tz
      );
      return `Diario a las ${pad2(local.hh)}:${pad2(local.mm)}`;
    }

    // WEEKLY@DayOfWeek@HH:MM
    if (expression.startsWith('WEEKLY@')) {
      const [, rest] = expression.split('WEEKLY@');
      const [dayCode, timeUtc] = (rest || '').split('@');
      if (dayCode && timeUtc) {
        const baseMondayUTC = new Date(Date.UTC(2000, 0, 3)); // 2000-01-03 fue lunes
        const targetDow = DOW_INPUTS[dayCode] ?? 1; // default Monday
        const baseDow = 1; // Monday
        const deltaDays = (targetDow - baseDow + 7) % 7;
        const refUtc = new Date(baseMondayUTC.getTime() + deltaDays * 86400000);

        const local = convertUtcHHMMToLocalOn(refUtc, timeUtc, tz);
        const dayNameEs = DOW_ES[local.dow];
        return `Semanal: ${dayNameEs} a las ${pad2(local.hh)}:${pad2(local.mm)}`;
      }
    }

    // MONTHLY@DD@HH:MM  (DD puede ser "L")
    if (expression.startsWith('MONTHLY@')) {
      const [, rest] = expression.split('MONTHLY@');
      const [dayStr, timeUtc] = (rest || '').split('@');
      if (dayStr && timeUtc) {
        // Elegimos enero 2000 como mes base (longitud fija por año bisiesto ya contemplada por días del mes).
        const monthBase = 1; // Enero
        let refUtc: Date;

        if (dayStr === 'L') {
          // último día del mes en UTC a HH:MM
          // Día 0 de febrero = último de enero
          const [hStr, mStr] = timeUtc.split(':');
          const H = parseInt(hStr, 10) || 0;
          const M = parseInt(mStr, 10) || 0;
          // último día de enero 2000
          refUtc = makeUtcDate(2000, monthBase + 1, 0, H, M);
        } else {
          const dd = Math.max(1, parseInt(dayStr, 10) || 1);
          refUtc = makeUtcDate(
            2000,
            monthBase,
            dd,
            ...(timeUtc.split(':').map(n => parseInt(n, 10)) as [
              number,
              number,
            ])
          );
        }

        const local = getLocalParts(refUtc, tz);

        // ¿Sigue siendo “último día del mes” en local?
        const lastInLocal = isLastDayLocal(
          local.y,
          local.m,
          local.d,
          tz,
          local.hh,
          local.mm
        );

        if (lastInLocal) {
          return `Mensual: Último día del mes a las ${pad2(local.hh)}:${pad2(local.mm)}`;
        }
        return `Mensual: Día ${local.d} a las ${pad2(local.hh)}:${pad2(local.mm)}`;
      }
    }

    // YEARLY@MM-DD@HH:MM  (DD puede ser "L")
    if (expression.startsWith('YEARLY@')) {
      const [, rest] = expression.split('YEARLY@');
      const [md, timeUtc] = (rest || '').split('@');
      if (md && timeUtc) {
        const [mmStr, ddStr] = md.split('-');
        const mm = Math.max(1, Math.min(12, parseInt(mmStr, 10) || 1));

        let refUtc: Date;
        if (ddStr === 'L') {
          // último día del mes mm (UTC) a HH:MM
          const [hStr, mStr] = timeUtc.split(':');
          const H = parseInt(hStr, 10) || 0;
          const M = parseInt(mStr, 10) || 0;
          refUtc = makeUtcDate(2000, mm + 1, 0, H, M); // día 0 del mes siguiente = último del actual
        } else {
          const dd = Math.max(1, parseInt(ddStr, 10) || 1);
          const [hStr, mStr] = timeUtc.split(':');
          const H = parseInt(hStr, 10) || 0;
          const M = parseInt(mStr, 10) || 0;
          refUtc = makeUtcDate(2000, mm, dd, H, M);
        }

        const local = getLocalParts(refUtc, tz);
        const lastInLocal = isLastDayLocal(
          local.y,
          local.m,
          local.d,
          tz,
          local.hh,
          local.mm
        );

        const monthName = MONTH_NAMES_ES[local.m - 1];
        if (lastInLocal) {
          return `Anual: Último día de ${monthName} a las ${pad2(local.hh)}:${pad2(local.mm)}`;
        }
        return `Anual: ${local.d} de ${monthName} a las ${pad2(local.hh)}:${pad2(local.mm)}`;
      }
    }

    // Si no coincide con ningún formato, devolver la expresión tal cual
    return expression;
  } catch (error) {
    console.error('Error formatting TimeSpanExpression (local TZ):', error);
    return expression;
  }
}
export function formatTimeSpan(durationStr: string | null | undefined): string {
  if (!durationStr) return '—';

  let d = 0,
    h = 0,
    m = 0,
    s = 0;

  // (D.HH:MM:SS)
  if (durationStr.includes('.')) {
    const [daysStr, timeStr] = durationStr.split('.');
    d = parseInt(daysStr || '0', 10);
    const [hoursStr, minutesStr, secondsStr] = timeStr.split(':');
    h = parseInt(hoursStr || '0', 10);
    m = parseInt(minutesStr || '0', 10);
    s = parseInt(secondsStr || '0', 10);
  } else {
    // Standard HH:MM:SS format
    const [hoursStr, minutesStr, secondsStr] = durationStr.split(':');
    h = parseInt(hoursStr || '0', 10);
    m = parseInt(minutesStr || '0', 10);
    s = parseInt(secondsStr || '0', 10);
  }

  const parts = [];
  if (d) parts.push(`${d} día${d > 1 ? 's' : ''}`);
  if (h) parts.push(`${h} hora${h > 1 ? 's' : ''}`);
  if (m) parts.push(`${m} min`);
  if (s && !d && !h && !m) parts.push(`${s} sec`); // solo mostrar segundos si no hay días, horas ni minutos

  return parts.length ? parts.join(' ') : '0s';
}
